import React from 'react';

import find from 'lodash/fp/find';
import getOr from 'lodash/fp/getOr';
import matchesProperty from 'lodash/fp/matchesProperty';

import { margin, useClassnames } from '@lumapps/classnames';
import { get } from '@lumapps/constants';
import {
    Alignment,
    AspectRatio,
    FlexBox,
    GenericBlock,
    Heading,
    Icon,
    Orientation,
    Size,
    Text,
    Thumbnail,
    ThumbnailVariant,
    Typography,
} from '@lumapps/lumx/react';
import { useSelector } from '@lumapps/redux/react';
import { useResponsive } from '@lumapps/responsive';
import { SpaceNavigation } from '@lumapps/spaces-navigation/components/SpaceNavigation';
import { SpaceSearchField } from '@lumapps/spaces-search';
import { isSearchInSpaceEarlyAccessSelector } from '@lumapps/spaces-search/selectors';
import {
    SubscriptionsActions,
    SubscriptionsActionsProps,
} from '@lumapps/subscriptions/components/SubscriptionsActions';
import { SubscriptionsTypes } from '@lumapps/subscriptions/types';
import { useTranslate } from '@lumapps/translations';
import { isSocialFeatureEnabled } from '@lumapps/user/ducks/selectors';

import { spaceVisibilityToLegacyPrivacyMap, SPACE_VISIBILITIES } from '../../constants';
import { useSpaceAccessRequest } from '../../hooks/useSpaceAccessRequest';
import { SpaceData, SpaceThumbnailDisplayType } from '../../types';
import { SpaceAboutDialog } from '../SpaceAboutDialog';
import { SpaceAccessRequest } from '../SpaceAccessRequest';
import { SpaceContributionButton } from '../SpaceContributionButton';

import './index.scss';

const CLASSNAME = 'space-header';

interface SpaceHeaderProps extends SpaceData {
    spaceId: string;
    isPreview?: boolean;
    isNavigationDisplayed?: boolean;
    beforeNavigation?: React.ReactNode[];
}

/**
 * Space header, shown on all pages of the Space feature.
 * */
export const SpaceHeader: React.FC<SpaceHeaderProps> = ({
    headerDisplayMode,
    enabledServices,
    providers,
    title,
    coverImage,
    visibility,
    folksonomyTags,
    myMembership,
    myPermissions,
    mySubscriptions,
    subscriptionSettings,
    spaceId,
    slug,
    description,
    isPreview = false,
    isNavigationDisplayed = true,
    beforeNavigation,
}) => {
    const { isMobileAppWebView } = get();

    const { block, element } = useClassnames(CLASSNAME);
    const { isMobile } = useResponsive();
    const { translateObject, translateKey } = useTranslate();
    const isSocialEnabled = useSelector(isSocialFeatureEnabled);
    const isSpaceSearchEnabled = useSelector(isSearchInSpaceEarlyAccessSelector);
    const { isLoading: isAccessRequestLoading, requestAccess } = useSpaceAccessRequest();

    if (!title || !visibility || !myPermissions || !mySubscriptions || !enabledServices || isMobileAppWebView) {
        return null;
    }

    const visibilityObject = find(
        matchesProperty('id', spaceVisibilityToLegacyPrivacyMap[visibility]),
        SPACE_VISIBILITIES,
    );

    const subscription: SubscriptionsActionsProps['subscription'] = {
        resourceId: spaceId,
        resourceType: SubscriptionsTypes.content,
        autoFetch: true,
        enableNotification: true,
        isSubscribed: getOr(false, 'isSubscribed', mySubscriptions),
        isNotified: getOr(false, 'isNotified', mySubscriptions),
        areNotificationsEnabledOnSubscription: getOr(
            false,
            'areNotificationsEnabledOnSubscription',
            subscriptionSettings,
        ),
    };

    return (
        <FlexBox
            orientation={Orientation.vertical}
            gap={Size.big}
            as="header"
            className={block({ preview: isPreview }, margin('bottom', Size.huge))}
        >
            {headerDisplayMode === SpaceThumbnailDisplayType.cover && !!coverImage && (
                <div className={element('banner')}>
                    <Thumbnail
                        fillHeight
                        variant={ThumbnailVariant.rounded}
                        aspectRatio={AspectRatio.horizontal}
                        alt=""
                        image={coverImage.url}
                        focusPoint={coverImage.focalPoint}
                    />
                </div>
            )}
            <GenericBlock gap={Size.huge} hAlign="center" className={margin('bottom', Size.regular)} wrap>
                {headerDisplayMode === SpaceThumbnailDisplayType.small && !!coverImage && (
                    <GenericBlock.Figure>
                        <Thumbnail
                            variant={ThumbnailVariant.rounded}
                            size={Size.l}
                            aspectRatio={AspectRatio.square}
                            alt=""
                            image={coverImage.url}
                            focusPoint={coverImage.focalPoint}
                        />
                    </GenericBlock.Figure>
                )}
                <GenericBlock.Content className={element('content')}>
                    <Heading
                        as="h1"
                        typography={isMobile ? Typography.custom.title3 : Typography.custom.title1}
                        className={element('title')}
                    >
                        {translateObject(title)}
                    </Heading>

                    {visibilityObject && (
                        <Text as="p" color="dark" colorVariant="L2" typography={Typography.body1}>
                            <Icon icon={visibilityObject.icon} />
                            {translateKey(visibilityObject.title)}
                        </Text>
                    )}
                </GenericBlock.Content>
                <GenericBlock.Actions
                    gap={Size.regular}
                    vAlign={Alignment.right}
                    noShrink
                    orientation={isMobile ? Orientation.vertical : Orientation.horizontal}
                >
                    <FlexBox orientation="horizontal" gap={Size.regular} noShrink>
                        <SpaceAboutDialog
                            title={title}
                            description={description}
                            visibility={visibility}
                            folksonomyTags={folksonomyTags}
                            isMobile={isMobile}
                            isPreview={isPreview}
                        />
                        {(subscription.isSubscribed || myPermissions.canSubscribe) && isSocialEnabled && (
                            <SubscriptionsActions
                                scope="space-subscription"
                                subscription={subscription}
                                isPreview={isPreview}
                            />
                        )}
                        {myPermissions.canContribute && <SpaceContributionButton isPreview={isPreview} />}
                    </FlexBox>
                    <SpaceAccessRequest
                        canRequest={myPermissions.canRequestMembership}
                        requestStatus={myMembership?.requestDetails?.status}
                        isLoading={isAccessRequestLoading}
                        requestAccess={requestAccess}
                    />
                </GenericBlock.Actions>
            </GenericBlock>
            {isNavigationDisplayed && (
                <FlexBox
                    orientation={Orientation.horizontal}
                    gap={Size.regular}
                    vAlign={Alignment.center}
                    className={element('navigation-container')}
                >
                    <SpaceNavigation
                        className={element('navigation')}
                        providers={providers}
                        spaceId={spaceId}
                        spaceSlug={translateObject(slug) as string}
                        before={beforeNavigation}
                    />
                    {isSpaceSearchEnabled && <SpaceSearchField isPreview={isPreview} />}
                </FlexBox>
            )}
        </FlexBox>
    );
};
